import React from 'react'
import PropTypes from 'prop-types'
import { Text, Icon, Button, Portal, Menu, MenuButton, MenuList, MenuOptionGroup, MenuItemOption, useControllableState } from '@chakra-ui/react'
import FiChevronUp from '@meronex/icons/fi/FiChevronUp'
import FiChevronDown from '@meronex/icons/fi/FiChevronDown'

import { FormLabelControl } from '../form-label-control'

export const Select = ({
  usePortal,
  isMulti,
  placeholder,
  options,
  defaultValue,
  value: valueProp,
  onChange,
  isDisabled,
  colorScheme,
  minWidth,
  height,
  propsOf,
  emptyContent,

  buttonProps,
  listProps,
  ...rest
}) => {
  const [value, setValue] = useControllableState({
    defaultValue,
    value: valueProp ?? '', // use empty string to make component always be controlled
    onChange,
  })
  const hasValue = React.useMemo(() => value?.length > 0, [value])
  const labels = React.useMemo(
    () =>
      options
        .filter((option) => (Array.isArray(value) ? value.includes(option.value) : Object.is(value, option.value)))
        .map((option) => option.label),
    [options, value],
  )

  return (
    <FormLabelControl label={placeholder} hasValue={hasValue}>
      {({ field }) => (
        <Menu colorScheme={colorScheme} closeOnSelect={!isMulti} {...propsOf.menu} {...rest}>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                minWidth={minWidth}
                height={height}
                variant='outline'
                pt={field?.isFocused || hasValue ? '1.625rem' : '1.125rem'}
                pb={field?.isFocused || hasValue ? '0.625rem' : '1.125rem'}
                role='group'
                rightIcon={<Icon as={isOpen ? FiChevronUp : FiChevronDown} marginLeft='auto' boxSize='1rem' _groupDisabled={{ color: 'gray.300' }} />}
                textAlign='left'
                color={hasValue ? 'gray.800' : 'gray.600'}
                fontSize='sm'
                borderColor='gray.200'
                bgColor='white'
                isDisabled={isDisabled}
                _invalid={{ borderColor: 'red.500' }}
                _active={{ borderColor: `${colorScheme}.500`, bgColor: 'gray.100', shadow: `outline-${colorScheme}` }}
                _selected={{ borderColor: `${colorScheme}.500`, shadow: `outline-${colorScheme}` }}
                _disabled={{ cursor: 'not-allowed', color: 'gray.700', bgColor: 'gray.50' }}
                _hover={{ bgColor: 'gray.100', _disabled: { bgColor: 'gray.50' } }}
                {...(field?.isInvalid ? { 'data-invalid': true } : {})}
                {...propsOf.button}
                {...buttonProps}
              >
                <Text isTruncated {...propsOf.buttonText}>
                  {labels?.join(', ')}
                </Text>
              </MenuButton>

              {React.createElement(
                usePortal ? Portal : React.Fragment,
                null,
                <MenuList paddingY='0.5rem' maxHeight='12.5rem' overflow='auto' {...listProps} {...propsOf.list}>
                  {options.length > 0 ? (
                    <MenuOptionGroup type={isMulti ? 'checkbox' : 'radio'} value={value} onChange={setValue} {...propsOf.group}>
                      {options.map((option, index) => (
                        <MenuItemOption key={index} paddingY='0.75rem' lineHeight='1.25' fontSize='sm' value={option.value} {...propsOf.option}>
                          {option.label}
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                  ) : (
                    emptyContent
                  )}
                </MenuList>,
              )}
            </>
          )}
        </Menu>
      )}
    </FormLabelControl>
  )
}

const ValuePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
Select.propTypes = {
  usePortal: PropTypes.bool,
  isMulti: PropTypes.bool,
  placeholder: PropTypes.node,
  colorScheme: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.node, value: PropTypes.string })).isRequired,
  defaultValue: ValuePropType,
  value: ValuePropType,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  emptyContent: PropTypes.node,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  propsOf: PropTypes.shape({
    menu: PropTypes.object,
    button: PropTypes.object,
    buttonText: PropTypes.object,
    list: PropTypes.object,
    group: PropTypes.object,
    option: PropTypes.object,
  }),

  // NOTE: keep the original usage to compatible with old version
  buttonProps: PropTypes.object,
  listProps: PropTypes.object,
}

Select.defaultProps = {
  colorScheme: 'brand',
  matchWidth: true, // from chakra <Menu>
  isMulti: false,
  usePortal: true,
  emptyContent: (
    <Text py='0.5rem' textAlign='center' fontSize='sm' color='gray.700'>
      No options
    </Text>
  ),
  minWidth: '17.5rem',
  height: '3.5rem',

  propsOf: {},
  buttonProps: {},
  listProps: {},
}
